import { useToast } from '@chakra-ui/react';
import { useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { CreateUserCohortDto } from '../../../models/user-cohort/dto/user-cohort.dto';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCreateCohort } from '../hooks/useCreateCohort';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const initialFormData: Omit<CreateUserCohortDto, 'organizationId'> = {
  name: '',
  description: '',
};

export default function CreateCohortModal({ isOpen, onClose }: Props) {
  const { selectedOrganization } = useSelectedOrganization();
  const [formData, setFormData] = useState<
    Omit<CreateUserCohortDto, 'organizationId'>
  >({ ...initialFormData });
  const { mutateAsync: create, isLoading } = useCreateCohort();
  const toast = useToast();

  const handleInputChange = (
    key: keyof typeof formData,
    value: string,
  ) => {
    setFormData({ ...formData, [key]: value });
  };

  const onModalClose = () => {
    setFormData({ ...initialFormData });
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedOrganization) return;

    try {
      await create({
        ...formData,
        organizationId: selectedOrganization.id,
      });

      toast({
        title: translate('cohort.created_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onModalClose();
    } catch (error) {
      toast({
        title: translate('cohort.create_failed'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const isNameValid = formData.name.trim().length > 0;
  const isFormValid = isNameValid;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onModalClose}
      title={translate('cohort.create_new_cohort')}
      onSubmit={handleSubmit}
      submitLabel={translate('cohort.create')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={isLoading}
      isSubmitDisabled={!isFormValid}
      size="md"
    >
      <FormStack>
        <FormField
          type="text"
          label={translate('cohort.cohort_name')}
          value={formData.name}
          onChange={(value) => handleInputChange('name', value)}
          placeholder={translate('cohort.cohort_name_placeholder')}
          error={!isNameValid && formData.name.length > 0 ? translate('cohort.name_required') : undefined}
          isRequired
        />

        <FormField
          type="textarea"
          label={translate('cohort.description_optional')}
          value={formData.description}
          onChange={(value) => handleInputChange('description', value)}
          placeholder={translate('cohort.description_placeholder')}
          textareaProps={{ rows: 4 }}
        />
      </FormStack>
    </FormModal>
  );
}
